import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSalesAgent } from '../hooks/useSalesAgent';
import GlowingCircle from './GlowingCircle';
import MeoChatModal from './MeoChatModal';
import { AgentState } from '../types';

const NOISE_THRESHOLD = 0.3;
const NOISE_SAMPLE_COUNT = 10;

const AgentWidget = ({ company, onPriceAgreed, onNegotiationStart }: { company: any; onPriceAgreed?: (price: number) => void; onNegotiationStart?: () => void }) => {
  const [agentMessages, setAgentMessages] = useState<string[]>([]);
  
  const onAgentTranscript = useCallback((text: string) => {
    setAgentMessages(prev => [...prev, text]);
  }, []);

  const { state, connect, disconnect, sendTextMessage, isSpeaking, userVolume, aiVolume, error } = useSalesAgent(company, onPriceAgreed, onAgentTranscript);
  const [showTooltip, setShowTooltip] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [noisy, setNoisy] = useState(false);
  const [navVisible, setNavVisible] = useState(true);
  const noiseSamples = useRef<number[]>([]);
  const lastScrollY = useRef(0);

  // Hide/show nav on scroll (mobile only)
  useEffect(() => {
    const SCROLL_THRESHOLD = 10;
    const handleScroll = () => {
      const currentY = window.scrollY;
      const delta = currentY - lastScrollY.current;
      if (Math.abs(delta) > SCROLL_THRESHOLD) {
        setNavVisible(delta < 0);
        lastScrollY.current = currentY;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hide tooltip after a few seconds
  useEffect(() => {
    if (state === AgentState.DISCONNECTED && showTooltip) {
      const timer = setTimeout(() => {
        setShowTooltip(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [state, showTooltip]);

  // Noise detection: track ambient volume when connected but not speaking
  useEffect(() => {
    if (state !== AgentState.CONNECTED || isSpeaking) {
      noiseSamples.current = [];
      setNoisy(false);
      return;
    }

    noiseSamples.current.push(userVolume);
    if (noiseSamples.current.length > NOISE_SAMPLE_COUNT) {
      noiseSamples.current.shift();
    }

    if (noiseSamples.current.length === NOISE_SAMPLE_COUNT) {
      const avg = noiseSamples.current.reduce((a, b) => a + b, 0) / NOISE_SAMPLE_COUNT;
      setNoisy(avg > NOISE_THRESHOLD);
    }
  }, [state, isSpeaking, userVolume]);

  // Fire onNegotiationStart once when user first interacts (voice or chat)
  const hasStartedRef = useRef(false);
  useEffect(() => {
    if ((showChat || state === AgentState.CONNECTED) && !hasStartedRef.current) {
      hasStartedRef.current = true;
      onNegotiationStart?.();
    }
  }, [showChat, state, onNegotiationStart]);

  const isActive = state === AgentState.CONNECTED;
  const effectiveVolume = isSpeaking ? aiVolume : userVolume;
  const colorClass = isSpeaking ? 'bg-blue-600' : 'bg-blue-500';

  const handleToggle = () => {
    if (state === AgentState.CONNECTED || state === AgentState.CONNECTING) {
      disconnect();
    } else {
      connect();
    }
  };

  const statusText =
    state === AgentState.CONNECTING
      ? 'Connecting...'
      : state === AgentState.CONNECTED
      ? isSpeaking
        ? 'Speaking...'
        : 'Listening...'
      : 'Tap to Ask';

  return (
    <>
      <div className={`fixed bottom-0 sm:bottom-2 xl:bottom-8 left-0 w-full sm:w-auto sm:left-auto sm:right-8 z-50 flex flex-col items-center sm:items-end transition-transform duration-300 ease-in-out ${navVisible ? 'translate-y-0' : 'translate-y-full'}`}>
        {/* Error Toast */}
        {error && (
          <div className="mb-3 p-2 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 rounded-lg shadow-lg max-w-[200px] text-xs border border-red-200 dark:border-red-800 animate-fade-in-up text-center">
            {error}
          </div>
        )}

        {/* Noise suggestion */}
        {noisy && !showChat && (
          <div className="mb-2 flex items-center gap-2 bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 px-3 py-2 animate-fade-in-up">
            <svg className="w-4 h-4 text-yellow-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-xs text-gray-700 dark:text-gray-300">Too noisy? Try</span>
            <button
              onClick={() => { if (state === AgentState.CONNECTED || state === AgentState.CONNECTING) disconnect(); setShowChat(true); }}
              className="text-xs font-semibold text-blue-600 hover:text-blue-700 underline"
            >
              Chat
            </button>
          </div>
        )}

        {/* Tooltip */}
        {showTooltip && state === AgentState.DISCONNECTED && (
          <div className="mb-3 p-3 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-100 dark:border-gray-800 max-w-[140px] relative animate-bounce-gentle">
            <h3 className="font-bold text-gray-800 dark:text-white text-xs">Have questions?</h3>
            <p className="text-gray-500 dark:text-gray-400 text-[10px] mt-0.5">
              Talk or chat with Meo
            </p>
            <div className="absolute -bottom-1.5 right-8 w-3 h-3 bg-white dark:bg-gray-900 transform rotate-45 border-b border-r border-gray-100 dark:border-gray-800" />
          </div>
        )}

        {/* Bottom Nav (mobile) / Floating Card (desktop) */}
        <div className="bg-white/90 dark:bg-black/90 border-t sm:border border-gray-200 dark:border-gray-800 shadow-2xl backdrop-blur-md flex flex-row sm:flex-col items-center justify-between sm:justify-center w-full sm:w-20 px-5 sm:px-0 py-0 sm:py-3 rounded-t-2xl sm:rounded-2xl rounded-b-none sm:rounded-b-2xl">
          {/* Chat button — left on mobile, inside card on desktop */}
          <button
            onClick={() => setShowChat(true)}
            className="flex flex-row sm:flex-col items-center gap-1 sm:gap-1 p-0.5 sm:p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Open chat"
          >
            <svg className="w-5 h-5 sm:w-4 sm:h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}>
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span className="hidden sm:block text-[10px] font-bold text-gray-500 dark:text-gray-400">Chat</span>
          </button>

          {/* Voice button — right on mobile, top on desktop */}
          <button
            onClick={handleToggle}
            disabled={state === AgentState.CONNECTING}
            className="flex flex-row sm:flex-col-reverse items-center gap-1.5 sm:gap-1 group relative focus:outline-none"
            aria-label={state === AgentState.CONNECTED ? 'Disconnect AI Agent' : 'Connect to AI Agent'}
          >
            <span className="text-[11px] sm:text-[10px] font-bold text-gray-500 dark:text-gray-400 whitespace-nowrap">
              {statusText}
            </span>
            <GlowingCircle
              volume={effectiveVolume}
              isActive={isActive}
              colorClass={colorClass}
            />
          </button>
        </div>
      </div>

      <MeoChatModal
        isOpen={showChat}
        onClose={() => setShowChat(false)}
        onPriceAgreed={onPriceAgreed}
        sendTextMessage={state === AgentState.CONNECTED ? sendTextMessage : undefined}
        agentMessages={agentMessages}
      />
    </>
  );
};

export default AgentWidget;
